import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS, getStadiumImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { useAdminAccess } from '../utilities/adminAccess';
import { authorizedPost, fileToBase64, getApiErrorMessage, getChampId } from '../utilities/stadiumsApi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';

const emptyForm = {
  enabled: true,
  name: '',
  description: '',
};

export default function ManagePitch() {
  const { stadiumId, pitchId } = useParams();
  const isNew = !pitchId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { canWrite } = useAdminAccess('stadiums');

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [stadiumName, setStadiumName] = useState('');

  useEffect(() => {
    loadPitch();
  }, [pitchId, stadiumId]);

  const goBackToStadium = () => {
    history.push(`/stadiums_management/${stadiumId}`);
  };

  const loadPitch = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setPhotoFile(null);
    setPhotoError('');

    const champId = getChampId();
    try {
      const stadiumResponse = await authorizedPost(dispatch, API_URLS.getAdminStadium, {
        id: Number(stadiumId),
        champ_id: champId || undefined,
      });
      setStadiumName(stadiumResponse.data?.name || '');
    } catch (error) {
      setStadiumName('');
    }

    if (isNew) {
      setForm(emptyForm);
      setPhotoPreview('');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminPitch, {
        id: Number(pitchId),
        champ_id: champId || undefined,
      });
      const pitch = response.data || {};
      setForm({
        enabled: Number(pitch.enabled) === 1,
        name: pitch.name || '',
        description: pitch.description || '',
      });
      const imagePath = pitch.image ? getStadiumImageUrl(pitch.image) : '';
      setPhotoPreview(imagePath ? `${imagePath}${imagePath.includes('?') ? '&' : '?'}v=${Date.now()}` : '');
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setErrors({ name: t('stdNameRequired') });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        stadium_id: Number(stadiumId),
        name: form.name.trim(),
        enabled: form.enabled ? 1 : 0,
        description: form.description.trim(),
      };
      if (photoFile) {
        payload.image = await fileToBase64(photoFile);
      }

      if (isNew) {
        await authorizedPost(dispatch, API_URLS.addPitch, payload);
        setMessage({ text: t('stPitchCreated'), type: 'success' });
        goBackToStadium();
      } else {
        await authorizedPost(dispatch, API_URLS.changePitch, {
          ...payload,
          id: Number(pitchId),
        });
        setPhotoFile(null);
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deletePitch, { id: Number(pitchId) });
      goBackToStadium();
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('navStadiums'), to: '/stadiums_management' },
                  { label: stadiumName || t('stEditStadium'), to: `/stadiums_management/${stadiumId}` },
                  { label: isNew ? t('stNewPitch') : t('stEditPitch') },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('stLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navStadiums'), to: '/stadiums_management' },
                { label: stadiumName || t('stEditStadium'), to: `/stadiums_management/${stadiumId}` },
                { label: isNew ? t('stNewPitch') : (form.name.trim() || t('stEditPitch')) },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                <div className="st-field st-field-wide">
                  <label className="st-check">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(event) => updateField('enabled', event.target.checked)}
                    />
                    {t('stShowOnSite')}
                  </label>
                </div>

                <div className="st-field st-field-wide">
                  <label>{t('stdName')} <span className="st-required">*</span></label>
                  <input
                    className={`st-input ${errors.name ? 'error' : ''}`}
                    value={form.name}
                    placeholder={t('stPitchNamePh')}
                    onChange={(event) => updateField('name', event.target.value)}
                  />
                  {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                </div>

                <div className="st-field st-field-wide">
                  <label>{t('stDescription')}</label>
                  <textarea
                    className="st-textarea"
                    value={form.description}
                    placeholder={t('stPitchDescriptionPh')}
                    onChange={(event) => updateField('description', event.target.value)}
                  />
                </div>

                <div className="st-field st-field-wide">
                  <label>{t('stPhoto')}</label>
                  <AdminPhotoUpload
                    preview={photoPreview}
                    error={photoError}
                    disabled={saving}
                    onFileChange={(file, error, preview) => {
                      if (error) {
                        setPhotoError(error);
                        return;
                      }
                      setPhotoError('');
                      setPhotoFile(file);
                      setPhotoPreview(preview);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {canWrite ? (
        <div className="st-actions">
          {!isNew ? (
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving || deleting}
              onClick={() => setDeleteVisible(true)}
            >
              {t('delete')}
            </button>
          ) : null}
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FiSave /> {saving ? t('stLoading') : (isNew ? t('stCreatePitch') : t('stdSaveChanges'))}
          </button>
        </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('stDeletePitchTitle')}</div>
          <p>{t('stDeletePitchText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
